import { inject, Injectable } from '@angular/core'
import { Notification, CustomErrData } from '../models/notification'
import { Message, NOTIFICATION_TOAST_VISIBILITY, NOTIFICATION_TYPES } from './notification.constants'
import { CONSTANTS } from '../../../../shared/constants'
import { MessageService } from 'primeng/api'

/**
 * central service which should be used to raise any notification to the user
 * it provides the notificationQueue as public observable
 * the subscription happens in NotificationComponent which then displays the notification as toast.
 */

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private messageService: MessageService = inject(MessageService)

  constructor() {}

  public addNotification(message: Message) {
    if (message.type === NOTIFICATION_TYPES.SUCCESS) {
      this.addSuccessNotification(message.message, undefined, message.delayInMS)
    } else if (message.type === NOTIFICATION_TYPES.INFO) {
      this.addInfoNotification(message.message, undefined, message.delayInMS)
    } else if (message.type === NOTIFICATION_TYPES.WARNING) {
      this.addWarnNotification(message.message, undefined, message.delayInMS)
    } else if (message.type === NOTIFICATION_TYPES.ERROR) {
      this.addErrorNotification(message.message, undefined, undefined, undefined, undefined, message.delayInMS)
    }
  }

  public addErrorNotification(
    message: string,
    detail?: string,
    timeStamp?: string,
    stacktrace?: string,
    errCode?: number,
    delayInMS?: number
  ) {
    const customErrData = new CustomErrData(CONSTANTS.PRIME_ICONS.EXCLAMATION_TRIANGLE, timeStamp, stacktrace, errCode)
    this.addMessage(message, NOTIFICATION_TYPES.ERROR, null, detail, delayInMS, customErrData)
  }

  public addSuccessNotification(message: string, detail?: string, delayInMS?: number) {
    this.addMessage(message, NOTIFICATION_TYPES.SUCCESS, CONSTANTS.PRIME_ICONS.CHECK_CIRCLE, detail, delayInMS)
  }

  public addInfoNotification(message: string, detail?: string, delayInMS?: number) {
    this.addMessage(message, NOTIFICATION_TYPES.INFO, CONSTANTS.PRIME_ICONS.INFO_CIRCLE, detail, delayInMS)
  }

  public addWarnNotification(message: string, detail?: string, delayInMS?: number) {
    this.addMessage(message, NOTIFICATION_TYPES.WARNING, CONSTANTS.PRIME_ICONS.EXCLAMATION_TRIANGLE, detail, delayInMS)
  }

  /**
   * a delay of 0 means that the message is sticky
   */
  private getDelayInMS(delayInMS: number, notificationType: string): number {
    return delayInMS === undefined || delayInMS === null ? NOTIFICATION_TOAST_VISIBILITY[notificationType] : delayInMS
  }

  private addMessage(
    message: string,
    notificationType: string,
    icon: string,
    detail: string,
    delayInMS: number,
    customErrData?: CustomErrData
  ) {
    const notification = new Notification(
      message,
      detail,
      notificationType,
      customErrData ?? new CustomErrData(icon),
      this.getDelayInMS(delayInMS, notificationType)
    )
    this.messageService.clear()
    this.messageService.add(notification)
  }
}
